import { Button, Col, Container, Modal, Row } from "react-bootstrap";
import { useCallback, useEffect, useRef, useState } from "react";

import { JSRoutes } from "../JSPage";
import { Preview } from "app/modules/shared";
import { useAuth } from "app/context/auth-context";
import { useHistory } from "react-router-dom";
import { UrlParamReplace } from "app/helpers/MiscUtils";
import { EndReason, InterviewSession, VideoSessionStatus } from "app/API";
import { InterviewSessionService } from "app/services/InterviewSessionService";
import { useAppContext } from "app/context/app-context";
import { AlertModal } from 'app/modules/shared/modals/AlertModal';
import { QueueUserService } from "app/services/QueueUserService";
import Sound from "app/modules/shared/Sound";
import { HMSRoomProvider, useHMSStore, selectRoomState, selectPeers } from "@100mslive/react-sdk";
import { BoothService } from "app/services/BoothService";
import { VJFPUserService } from "app/services/VJFPUserService";
import { RxSubscription } from "app/RxSubscriptions";

const COUNTDOWN_TIMEOUT = 21;
const SOUND_INTERVAL = 3; // Play sound every 3 seconds

const PermissionDemoComponent = () => {
  const roomState = useHMSStore(selectRoomState);

  useEffect(() => {
    // This component is ready to listen for room state changes if needed for permission handling
    // Permission errors from HMS SDK are handled in the error event listeners
  }, [roomState]);

  const handleDemoPermissions = () => {
    // Force a permissions request for demo purposes
    navigator.mediaDevices.getUserMedia({ audio: true, video: true })
      .then(stream => {
        // Stop the stream immediately after getting permissions
        stream.getTracks().forEach(track => track.stop());
        alert('Permissions granted! Audio and video should now work.');
      })
      .catch(error => {
        console.log('Demo permissions error:', error);
      });
  };

  return (
    <div>
      <button onClick={handleDemoPermissions} style={{ display: 'none' }} id="demo-perms" />
    </div>
  );
};

export const InterviewCallModal = () => {
  const [showInterviewCallingModal, setShowInterviewCallingModal] = useState<boolean>(false);
  const [isStartedAudio, setIsStartedAudio] = useState<boolean>(false);
  const [isStartedVideo, setIsStartedVideo] = useState<boolean>(false);
  const [interviewSession, setInterviewSession] = useState<InterviewSession | null>(null);
  const [showSessionTimeOutModal, setShowSessionTimeOutModal] = useState<boolean>(false);
  const [roomId, setRoomId] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [recruiterName, setRecruiterName] = useState<string>('');
  const [countdownSeconds, setCountdownSeconds] = useState<number>(COUNTDOWN_TIMEOUT);
  const [showPermissionErrorModal, setShowPermissionErrorModal] = useState<boolean>(false);
  const [isAcceptingCall, setIsAcceptingCall] = useState<boolean>(false);
  const [playSound, setPlaySound] = useState<boolean>(false);

  const history = useHistory();
  const { user } = useAuth();
  const { jobFair } = useAppContext();
  const subscribedIds = useRef<Set<string>>(new Set<string>());
  const isMounted = useRef(true);
  const soundIntervalRef = useRef<NodeJS.Timeout>();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
      }
    };
  }, []);

  // Handle sound playback when modal is shown
  useEffect(() => {
    if (showInterviewCallingModal && !isAcceptingCall) {
      // Play sound immediately when modal opens
      setPlaySound(true);

      // Set up interval to play sound every SOUND_INTERVAL seconds
      soundIntervalRef.current = setInterval(() => {
        if (isMounted.current) {
          setPlaySound(false);
          // Use setTimeout to trigger re-render and play again
          setTimeout(() => {
            if (isMounted.current) {
              setPlaySound(true);
            }
          }, 100);
        }
      }, SOUND_INTERVAL * 1000);
    } else {
      // Stop sound when modal closes or call is accepted
      setPlaySound(false);
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
        soundIntervalRef.current = undefined;
      }
    }

    return () => {
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
        soundIntervalRef.current = undefined;
      }
    };
  }, [showInterviewCallingModal, isAcceptingCall]);

  useEffect(() => {
    if (!showInterviewCallingModal) return;

    let timeoutId: NodeJS.Timeout;

    if (countdownSeconds <= 0) {
      handleTimeout();
    } else {
      timeoutId = setTimeout(() => {
        if (isMounted.current) {
          setCountdownSeconds(prev => prev - 1);
        }
      }, 1000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [countdownSeconds, showInterviewCallingModal]);

  useEffect(() => {
    if (!user) return;

    const subscription = RxSubscription.onChangeInterviewSessionByCandidateIdObservable().subscribe(async changeEventInterviewSession => {
      try {
        if (changeEventInterviewSession?.mode !== 'create') return;

        const interviewSessionObj = changeEventInterviewSession?.value;
        if (!isMounted.current ||
          !interviewSessionObj ||
          subscribedIds.current.has(interviewSessionObj.id) ||
          interviewSessionObj.status !== VideoSessionStatus.AWAITING) {
          return;
        }

        if (sessionStorage.getItem('isInterviewCallModalActive-' + interviewSessionObj.id) != null) return;

        console.log(interviewSessionObj.recruiterId + ' Recruiter calling');
        if (interviewSessionObj.candidateId !== user.id) return;

        // Show modal immediately
        if (isMounted.current) {
          setRoomId(interviewSessionObj.roomId);
          setShowInterviewCallingModal(true);
          setIsAcceptingCall(false);
          setCountdownSeconds(COUNTDOWN_TIMEOUT);
          sessionStorage.setItem('isInterviewCallModalActive-' + interviewSessionObj.id, '1');
          subscribedIds.current.add(interviewSessionObj.id);
          setInterviewSession(interviewSessionObj);
        }

        if (jobFair?.id) {
          const booth = await new BoothService().getBoothById(jobFair.id, interviewSessionObj.boothId);
          const recruiterUser = await new VJFPUserService().getById(user.organizationId, interviewSessionObj.recruiterId);

          if (isMounted.current) {
            setCompanyName(booth?.name || '');
            setRecruiterName(recruiterUser ? `${recruiterUser.firstName} ${recruiterUser.lastName}` : '');
          }
        }
      } catch (error) {
        console.error('Error in subscription handler:', error);
      }
    });

    return () => {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    };
  }, [user, jobFair]);

  const handleTimeout = async () => {
    try {
      if (isMounted.current) {
        localStorage.removeItem('isInterviewCallModalActive');
        setShowSessionTimeOutModal(true);
        setShowInterviewCallingModal(false);
        setCountdownSeconds(COUNTDOWN_TIMEOUT);
        await endSession();
      }
    } catch (error) {
      console.error('Error in handleTimeout:', error);
    }
  };

  const endSession = useCallback(async () => {
    if (!jobFair?.id || !user?.id || !interviewSession?.id) return;

    try {
      await new QueueUserService().endInterview(
        jobFair.id,
        interviewSession.boothId,
        interviewSession.id,
        user.id,
        EndReason.TIMED_OUT
      );
    } catch (error) {
      console.error('Error ending session:', error);
    }
  }, [jobFair?.id, user?.id, interviewSession]);

  const acceptCall = useCallback(async () => {
    setIsAcceptingCall(true);
    if (!jobFair?.id || !interviewSession?.id) return;

    try {
      const _isStartedAudio = isStartedAudio;
      const _isStartedVideo = isStartedVideo;

      const i = await new InterviewSessionService().getById(jobFair.id, interviewSession.id);
      if (i?.status !== 'AWAITING') {
        navigateToLobby();
        return;
      }

      const _interviewSession = await new InterviewSessionService().updateStatus(
        jobFair.id,
        interviewSession.id,
        VideoSessionStatus.IN_PROGRESS
      );

      if (_interviewSession && isMounted.current) {
        const url = UrlParamReplace(JSRoutes.Interview, ':sessionId', interviewSession.id);

        if (user?.id) {
          await new QueueUserService().updateJobSeekerJoined(
            jobFair.id,
            user.id,
            _interviewSession.queueUserId,
            _interviewSession.recruiterId
          );
        }

        if (isMounted.current) {
          setShowInterviewCallingModal(false);
          setCountdownSeconds(COUNTDOWN_TIMEOUT);
          history.push(`${url}?startVideo=${_isStartedVideo}&startAudio=${_isStartedAudio}`);
        }
      }
    } catch (error) {
      console.error('Error accepting call:', error);
    }
  }, [jobFair?.id, interviewSession?.id, isStartedAudio, isStartedVideo, user?.id, history]);

  const navigateToLobby = useCallback(() => {
    setShowSessionTimeOutModal(false);
    history.push(UrlParamReplace(JSRoutes.Lobby, '', ''));
  }, [history]);

  return (
    <>
      <Modal show={showInterviewCallingModal}>
        <Modal.Header>
          <Modal.Title>Video Call</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Row>
              <Col>
                <Sound play={playSound} />
                <Preview
                  roomId={roomId}
                  width="100%"
                  height="200px"
                  isPreviewActive={showInterviewCallingModal}
                  onAudioStarted={(started) => isMounted.current && setIsStartedAudio(started)}
                  onVideoStarted={(started) => isMounted.current && setIsStartedVideo(started)}
                  showDeviceSetting={false}
                />
                <PermissionDemoComponent />
              </Col>
            </Row>
            <Row>
              <p className="h4">
                <strong className="text-success">{companyName}</strong> - Recruiter (<strong className="text-success">{recruiterName}</strong>) is waiting for interview, please click <strong className="text-success">Join</strong>.
              </p>
            </Row>

          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Container>
            <Row>
              Seconds remaining before this session times out: {countdownSeconds}
            </Row>
          </Container>
          <Button variant='success' onClick={acceptCall}>
            Join
          </Button>
        </Modal.Footer>
      </Modal>

      <AlertModal
        title="Camera/Microphone Permission Required"
        show={showPermissionErrorModal}
        onOK={() => setShowPermissionErrorModal(false)}
      >
        <p>To join the video interview, you need to allow camera and microphone permissions.</p>
        <h6>How to enable permissions:</h6>
        <ol>
          <li>Look for the camera/shield icon in your browser's address bar</li>
          <li>Click it and select "Allow" for both camera and microphone</li>
          <li>Refresh the page if needed</li>
        </ol>
        <p><strong>For Chrome:</strong> Click lock icon → Site settings → Change camera/microphone to "Allow"</p>
        <p><strong>For Firefox:</strong> Click shield icon → Allow camera and microphone</p>
        <p>To test permissions, use the developer tools console and run:</p>
        <code>navigator.mediaDevices.getUserMedia({'{audio: true, video: true}'}).then(console.log)</code>
      </AlertModal>

      <AlertModal
        title="Interview ended -- This session has timed out."
        show={showSessionTimeOutModal}
        onOK={navigateToLobby}
      />
    </>
  );
};